import { Box, BoxProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import useResizeObserver from "@react-hook/resize-observer";
import { forwardRef, Ref, useImperativeHandle, useRef, useState } from "react";

import {
  Selectable,
  SelectableProps as SelectablePropsInterface,
} from "@/components/ui-v2/Selectable/Selectable";

import { getFontSize } from "./utils";

export interface TileBaseProps extends BoxProps {
  slots?: {
    bottomRightEl?: React.ElementType;
    bottomLeftEl?: React.ElementType;
    topRightEl?: React.ElementType;
    topLeftEl?: React.ElementType;
  };
  slotProps?: {
    bottomRightEl?: Record<string, any>;
    bottomLeftEl?: Record<string, any>;
    topRightEl?: Record<string, any>;
    topLeftEl?: Record<string, any>;
  };
  isSelectable?: boolean;
  isSelected?: SelectablePropsInterface["isSelected"];
  isFocused?: SelectablePropsInterface["isFocused"];
  SelectableProps?: Partial<SelectablePropsInterface>;
}

export const classes = {
  root: "TileBase-root",
  bottomRightEl: "TileBase-bottomRightEl",
  bottomLeftEl: "TileBase-bottomLeftEl",
  topRightEl: "TileBase-topRightEl",
  topLeftEl: "TileBase-topLeftEl",
  selectable: "TileBase-selectable",
};

const StyledBox = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isSelected" && prop !== "thumbnailWidth",
})<{ isSelected?: boolean; thumbnailWidth: number }>(
  ({ theme, thumbnailWidth }) => ({
    transition: "opacity 0.1s ease-in-out, background 0.1s ease-in-out",
    position: "relative",
    aspectRatio: "1/1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    overflow: "hidden",
    fontSize: getFontSize(thumbnailWidth),
    background: `repeating-conic-gradient(${theme.palette.secondary[8]} 0 25%, ${theme.palette.secondary[6]}  0 50%) 50% / 10% 10%`,

    [`& .${classes.bottomRightEl}, & .${classes.bottomLeftEl}, & .${classes.topRightEl}, & .${classes.topLeftEl}`]:
      {
        visibility: thumbnailWidth > 0 ? "visible" : "hidden",
      },
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "contain",
    },

    "& video": {
      width: "100%",
      height: "100%",
      objectFit: "contain",
    },
  }),
);

const StyledCornerEl = styled("div")({
  position: "absolute",
  zIndex: 1,
});

const BottomRightEl = styled(StyledCornerEl)({
  bottom: 0,
  right: 0,
});

const BottomLeftEl = styled(StyledCornerEl)({
  bottom: 0,
  left: 0,
});

const TopRightEl = styled(StyledCornerEl)({
  top: 0,
  right: 0,
});

const TopLeftEl = styled(StyledCornerEl)({
  top: 0,
  left: 0,
});

const DEFAULT_SLOT_PROPS = {
  bottomRightEl: {},
  bottomLeftEl: {},
  topRightEl: {},
  topLeftEl: {},
};

export const TileBase = forwardRef(
  (props: TileBaseProps, ref: Ref<unknown>) => {
    const {
      children,
      slots,
      isSelected = false,
      isSelectable = false,
      isFocused = false,
      slotProps = DEFAULT_SLOT_PROPS,
      SelectableProps = {},
      ...rest
    } = props;
    const containerRef = useRef<HTMLDivElement>(null);
    const [thumbnailWidth, setThumbnailWidth] = useState(0);

    useResizeObserver(containerRef, (entry) => {
      setThumbnailWidth(entry.target?.clientWidth);
    });

    useImperativeHandle(ref, () => containerRef.current, []);

    const tile = (
      <StyledBox
        ref={containerRef}
        className={classes.root}
        thumbnailWidth={thumbnailWidth}
        {...rest}
      >
        {slots?.topLeftEl && (
          <TopLeftEl className={classes.topLeftEl}>
            <slots.topLeftEl {...slotProps.topLeftEl} />
          </TopLeftEl>
        )}
        {slots?.topRightEl && (
          <TopRightEl className={classes.topRightEl}>
            <slots.topRightEl {...slotProps.topRightEl} />
          </TopRightEl>
        )}
        {slots?.bottomLeftEl && (
          <BottomLeftEl className={classes.bottomLeftEl}>
            <slots.bottomLeftEl {...slotProps.bottomLeftEl} />
          </BottomLeftEl>
        )}
        {slots?.bottomRightEl && (
          <BottomRightEl className={classes.bottomRightEl}>
            <slots.bottomRightEl {...slotProps.bottomRightEl} />
          </BottomRightEl>
        )}
        {children}
      </StyledBox>
    );

    if (!isSelectable) {
      return tile;
    }

    return (
      <Selectable
        isSelected={isSelected}
        isFocused={isFocused}
        className={classes.selectable}
        {...SelectableProps}
      >
        {tile}
      </Selectable>
    );
  },
);
